import { Slide } from '../components/SlideComponents/slide.types';
import { getPurelyPremiumGeneratorIds } from '../components/dynamicPages/CreatePage/test-prep-tab/TestPrep.Utils';

/**
  // These are generators that we've decided should be treated as premium even thugh some variants of it are free
 */
export const GENERATORS_WITH_PREMIUM_VARIANTS = new Set<string>([
  'b6db9100-70ad-480c-a43e-7fbc63719c9d', // Extended Constructed Response
  'd57e0069-e424-4d5c-a290-d0ab5c0b6f5f', // Writing Performance Task
  '72aa8efc-c52c-44e3-b44c-313f418818ee', // Math Constructed Response Question (1-2pt)
  '846b2223-dbf5-4dff-b4f2-bc9c7727669d', // Math Constructed Response Question (3-4pt)
  'd5a1b57c-3015-4d42-b3a6-c6f00862f621', // Math Performance Task Simulation
  'de25e0f7-6681-4951-9434-e9975769a411', // Paired Passage Extended Constructed Response
  '56d281ba-7a95-49a2-8683-ad094d8b6bf1', // Paired Passage Short Constructed Response
  'b98defc8-6f19-404f-9089-02a7b498bb14', // Performance Task Component (1-2pt)
  '69c79e12-3f3c-48c1-be0d-87e657538a83', // Performance Task Component (3-4pt)
  '51c4e5bd-7abc-449d-8f20-fe27f71fcc96', // Use My Materials - Extended Constructed Response
  '32457ab2-b396-48df-a951-e51524445442', // Use My Materials - Paired Passage Extended Constructed Response
  'e1ecadbc-2623-4c55-9bf4-f0cab0d637d7', // Use My Materials - Paired Passage Short Constructed Response
  'bbf41d56-5abf-4e5b-b800-f203c91d3a4c', // Use My Materials - Short Constructed Response
  'dfca9456-4a81-4859-89bf-0f9f98f4f411', // Use My Materials - Writing Performance Task
]);

// Generated by running `npx tsx scripts/computePremiumGeneratorIds.ts`: generators with all premium variants
export const PURELY_PREMIUM_GENERATOR_IDS = new Set<string>([
  '24029959-6df4-41e4-b8e9-064217305158', // config.testprep.caasppScienceSCR
  '36c2b7ec-602b-4075-a5e9-b482fe11ac85', // config.testprep.staarRLASCRRevisingSentences
  '4c533a51-cb74-4999-ab1a-5c0280a3f452', // config.testprep.elpacWriteAboutAnExperience
  '79d45849-90f1-45ad-a8e8-d39aea2b4a9b', // config.testprep.nystepMath2PointWhiteboardConstructedResponse
  '7a0238e0-9f99-4a5f-b57c-65f05aeefacd', // config.testprep.staarSocialStudiesSCRUpload
  '7e283104-ade3-498d-89d5-b9e52aaf3cad', // config.testprep.staarSocialStudiesSCR
  '8349c39f-e1d4-4917-b279-572929a79b6a', // config.testprep.nystepMath3PointWhiteboardConstructedResponse
  '9f8366a9-8f4e-473c-aa57-efb933340cb9', // config.testprep.caasppScienceSCRUpload
  'ca6cd604-e032-4390-a1d9-92c95ba91e34', // config.testprep.staarScienceSCR
  'eb0e5bb7-2174-4585-9583-19f1cc64f388', // config.testprep.staarScienceSCRUpload
]);

/**
 * Returns the union of mixed-tier composite generator UUIDs and
 * purely-premium standalone generator IDs derived at runtime from the
 * test prep config tree.
 */
// export function getAllBlockedPremiumGeneratorIds(): Set<string> {
//   return new Set([...GENERATORS_WITH_PREMIUM_VARIANTS, ...PURELY_PREMIUM_GENERATOR_IDS]);
// }

export function getAllBlockedPremiumGeneratorIds(): Set<string> {
  return new Set([...GENERATORS_WITH_PREMIUM_VARIANTS, ...PURELY_PREMIUM_GENERATOR_IDS]);
}

/**
 * Detects whether a presentation contains premium test prep content.
 *
 * Detection priority:
 * 1. Any slide has `isPremiumContent === true` -> premium (precise, new presentations)
 * 2. Every slide has `isPremiumContent === false` -> NOT premium, even if the
 *    generator UUID would otherwise match. This is how the internal-only
 *    "Save generated lessons as free" toggle (see
 *    `utils/saveGeneratedLessonAsFreeUtils.ts`) opts a lesson out of the paywall.
 * 3. No `isPremiumContent` flags -> fall back to UUID heuristic (old presentations)
 *
 * @param slides - Array of slides from the presentation
 * @returns true if the presentation contains premium test prep content
 */
export function isPremiumTestPrepPresentation(slides: Slide[]): boolean {
  if (!slides || slides.length === 0) {
    return false;
  }

  if (slides.some(slide => slide.isPremiumContent === true)) {
    return true;
  }

  // Explicit "not premium" stamp on every slide overrides the UUID heuristic.
  // TODO(internal-save-as-free): this is per-slide-fragile — adding a single
  // unflagged slide to a previously stamped lesson causes `every === false` to
  // fail and the UUID heuristic to flip the lesson back to premium. If we keep
  // this beyond the internal pilot, prefer a template-level marker (e.g.
  // `template.metadata.savedAsFree: true`) that survives slide edits.
  if (slides.every(slide => slide.isPremiumContent === false)) {
    return false;
  }

  const blockedIds = getAllBlockedPremiumGeneratorIds();
  return slides.some(slide => {
    if (!slide.generatorId) {
      return false;
    }
    const uuid = slide.generatorId.split('::')[0];
    return blockedIds.has(uuid);
  });
}

/**
 * For backwards compatibility with old presentations that don't have
 * `isPremiumContent` on their slides: stamps the flag on slides whose
 * generatorId matches the premium UUID heuristic.
 *
 * The backend (`hosts.ts`) checks `slide.isPremiumContent` to record
 * premium test prep plays, so this must be called before creating a game
 * from an old presentation.
 *
 * Returns the original array unchanged if any slide already has the flag.
 */
export function stampPremiumContentOnSlides(slides: Slide[]): Slide[] {
  if (!slides || slides.length === 0) return slides;
  if (slides.some(s => s.isPremiumContent === true)) return slides;
  // Explicit "not premium" stamp (set by the internal "Save generated lessons
  // as free" toggle) opts the lesson out of UUID-heuristic re-stamping.
  if (slides.every(s => s.isPremiumContent === false)) return slides;

  const blockedIds = getAllBlockedPremiumGeneratorIds();
  let anyStamped = false;

  const stamped = slides.map(slide => {
    if (!slide.generatorId) return slide;
    const uuid = slide.generatorId.split('::')[0];
    if (blockedIds.has(uuid)) {
      anyStamped = true;
      return { ...slide, isPremiumContent: true as const };
    }
    return slide;
  });

  return anyStamped ? stamped : slides;
}
